import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Pusher from 'pusher-js';
import { UserAgentApplication } from 'msal';

import { getGoogleSettings, getMicrosoftSettings, getPusherConfig } from 'services/requests';

import { IMicrosoftSettings, IPusherConfig } from 'services/types';
import { IParams, IRedirectState } from 'types';

import { ReactComponent as MicrosoftIcon } from 'assets/microsoft.svg';
import { ReactComponent as GoogleIcon } from 'assets/google.svg';

import Loader from 'components/Loader/Loader';
import { validateReturnOrigin } from '../../signIn/openerOrigin';

interface IProps {
  onSuccess: () => void;
}

const LoginButtons: React.FC<IProps> = ({ onSuccess }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pusherConfig, setPusherConfig] = useState<IPusherConfig | undefined>(undefined);
  const [params, setParams] = useState<IParams | undefined>(undefined);
  const [redirectMicrosoftToken, setRedirectMicrosoftToken] = useState<string | null>(null);
  const redirectMicrosoftTokenRef = useRef<string | null>(null);
  const [redirectState, setRedirectState] = useState<IRedirectState | null>(null);
  const [microsoftSettings, setMicrosoftSettings] = useState<IMicrosoftSettings | null>(null);
  const [microsoftSettingsLoading, setMicrosoftSettingsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const pusher = useMemo(() => {
    if (pusherConfig?.cluster && pusherConfig?.key) {
      return new Pusher(pusherConfig.key, {
        cluster: pusherConfig.cluster,
        channelAuthorization: {
          transport: 'ajax',
          endpoint: `${import.meta.env.VITE_BASE_API_URL}/common/authorize-auth-pusher`,
        },
      });
    }
  }, [pusherConfig]);

  // Sends the auth result to whoever asked for it.
  //
  // A caller that sent returnOrigin (today, only useAuthApp.ts) is on the new contract and
  // is listening on postMessage, not Pusher - if the opener or its allow-listed origin isn't
  // there, there is nobody to send a Pusher message to either, so this shows an error and
  // sends nothing rather than quietly firing Pusher into a channel nothing reads and
  // reporting success (a legacy caller that never set up a listener would otherwise appear
  // to succeed while the portal spins for 5 minutes).
  //
  // A caller with no returnOrigin is on the legacy contract (Web LoginForm.tsx's desktopMode
  // path, Pod Login.tsx) and still gets today's Pusher mechanism unchanged - see
  // doc:implementation on BOARD:37 for why the popup can't tell that case apart from a
  // crafted link, and why it isn't closed by this change.
  const handleAuthResult = (
    data: { token: string; type: string },
    nonce: string | undefined,
    returnOrigin: string | null,
  ): void => {
    if (returnOrigin) {
      const validatedOrigin = validateReturnOrigin(returnOrigin);
      if (window.opener && validatedOrigin) {
        try {
          window.opener.postMessage({ ...data, nonce }, validatedOrigin);
          onSuccess();
        } finally {
          setLoading(false);
        }
      } else {
        setErrorMessage('Please start sign-in again from the app.');
        setLoading(false);
      }
      return;
    }

    try {
      if (pusher?.key && nonce) {
        setLoading(true);
        const ch = pusher.subscribe(`private-${nonce}`);

        ch.bind('pusher:subscription_succeeded', () => {
          ch.trigger('client-message', data);
          onSuccess();
          setLoading(false);
        });
      }
    } catch (e) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = (): void => {
    try {
      setErrorMessage(null);
      if (microsoftSettingsLoading || !microsoftSettings) {
        setErrorMessage('Microsoft auth is not ready yet. Please try again in a moment.');
        setLoading(false);
        return;
      }

      setLoading(true);
      const redirectUri = window.location.href.split('#')[0].split('?')[0];
      const msalObject = new UserAgentApplication({
        auth: {
          clientId: microsoftSettings.auth.clientId,
          authority: microsoftSettings.auth.authority,
          redirectUri,
        },
      });

      // sessionId is the legacy name Web LoginForm.tsx (ConnectWise desktop) and Pod Login.tsx
      // still send - they're on the Pusher contract (no returnOrigin), not the postMessage one.
      const nonce = params?.nonce ?? searchParams.get('nonce') ?? searchParams.get('sessionId') ?? undefined;
      const returnOrigin = params?.returnOrigin ?? searchParams.get('returnOrigin');

      console.log('Starting Microsoft loginRedirect...');
      try {
        msalObject.loginRedirect({
          scopes: ['User.Read'],
          state: JSON.stringify({ nonce, returnOrigin }),
        });
        // If redirect is blocked/suppressed, user will stay on loader.
        // Provide a small fallback tologinPopup surface the issue.
        window.setTimeout(() => {
          setLoading(false);
          setErrorMessage('Microsoft login did not start. Please check console and popup/redirect permissions.');
        }, 8000);
      } catch (e) {
        console.error('loginRedirect failed:', e);
        setErrorMessage('Microsoft loginRedirect failed. Check console for details.');
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  type GoogleOAuth2Window = Window & {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token: string }) => void;
          }) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  };

  const win = window as unknown as GoogleOAuth2Window;

  const loadGoogleScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (win.google?.accounts?.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (win.google?.accounts?.oauth2) {
          resolve();
        } else {
          reject(new Error('Google script loaded but OAuth2 not available.'));
        }
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script.'));
      };

      document.head.appendChild(script);
    });
  };

  const handleGoogleLogin = async (): Promise<void> => {
    setLoading(true);
    try {
      const { clientId } = await getGoogleSettings();
      await loadGoogleScript();

      const nonce = params?.nonce ?? searchParams.get('nonce') ?? searchParams.get('sessionId') ?? undefined;
      const returnOrigin = params?.returnOrigin ?? searchParams.get('returnOrigin');

      const client = win.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'email profile openid',
        callback: (response: { access_token: string }) => {
          if (response?.access_token) {
            handleAuthResult({ type: 'google', token: response.access_token }, nonce, returnOrigin);
          } else {
            console.error('No access token received');
            setLoading(false);
          }
        },
      });

      client.requestAccessToken();
    } catch (err) {
      console.error('Google login failed:', err);
      setLoading(false);
    }
  };

  const onMount = useCallback(() => {
    setLoading(true);
    getPusherConfig()
      .then(res => {
        setPusherConfig(res);

        const type = searchParams.get('type');
        // sessionId is the legacy name the Web/Pod openers still send on the Pusher contract.
        const nonce = searchParams.get('nonce') ?? searchParams.get('sessionId');
        const returnOrigin = searchParams.get('returnOrigin');
        if (res && type && nonce) {
          setParams({ type, nonce, returnOrigin });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Prefetch MSAL settings so button click doesn't rely on async fetch.
  useEffect(() => {
    setMicrosoftSettingsLoading(true);
    getMicrosoftSettings()
      .then(res => {
        setMicrosoftSettings(res);
      })
      .catch(err => {
        console.error('getMicrosoftSettings failed:', err);
        setErrorMessage('Failed to load Microsoft auth settings.');
      })
      .finally(() => {
        setMicrosoftSettingsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!microsoftSettings) return;

    const redirectUri = window.location.href.split('#')[0].split('?')[0];
    const msalObject = new UserAgentApplication({
      auth: {
        clientId: microsoftSettings.auth.clientId,
        authority: microsoftSettings.auth.authority,
        redirectUri,
      },
    });

    // MSAL redirect flow stores the response internally; read it here after redirect back.
    // The nonce/returnOrigin travel in MSAL's own `state` param, not the URL or localStorage -
    // the URL has no query string left by the time we're back (redirectUri strips it), and
    // AC4 requires no sessionId/nonce key ever written to localStorage.
    msalObject.handleRedirectCallback((authErr, response) => {
      if (authErr) {
        console.error('MSAL redirect error:', authErr);
        return;
      }

      if (response?.idToken?.rawIdToken) {
        redirectMicrosoftTokenRef.current = response.idToken.rawIdToken;
        setRedirectMicrosoftToken(response.idToken.rawIdToken);

        try {
          const parsed = JSON.parse(response.accountState) as IRedirectState;
          if (parsed?.nonce) {
            setRedirectState(parsed);
          }
        } catch (e) {
          console.error('Unable to parse MSAL state on redirect return:', e);
        }
      }
    });
  }, [microsoftSettings]);

  useEffect(() => {
    if (redirectMicrosoftToken && redirectState) {
      handleAuthResult(
        { type: 'microsoft', token: redirectMicrosoftToken },
        redirectState.nonce,
        redirectState.returnOrigin,
      );
      // Prevent sending the same token multiple times.
      redirectMicrosoftTokenRef.current = null;
      setRedirectMicrosoftToken(null);
      setRedirectState(null);
    }
  }, [redirectMicrosoftToken, redirectState]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  if (loading) return <Loader />;
  return (
    <div className="list">
      <button className="button" onClick={handleMicrosoftLogin}>
        Microsoft
        <MicrosoftIcon />
      </button>

      <button className="button" onClick={() => {
        handleGoogleLogin()
      }}>
        Google
        <GoogleIcon />
      </button>
      {errorMessage && <div className="error-message">{errorMessage}</div>}
    </div>
  );
};

export default LoginButtons;
